// AlgoTab — Strategy builder, signals, backtest
import React, { useState } from 'react';
import { useTrading } from '../../../context/TradingContext';
import { useMindfulAlgo } from '../../../hooks/useMindfulAlgo';
import StrategyBuilder from '../algo/StrategyBuilder';
import SignalMonitor from '../algo/SignalMonitor';
import BacktestRunner from '../algo/BacktestRunner';
import ExecutionLog from '../algo/ExecutionLog';
import { FaPlay, FaStop, FaPlus, FaRobot } from 'react-icons/fa';

export default function AlgoTab() {
  const { connectionStatus } = useTrading();
  const {
    strategies, signals, execLog, backtestResults,
    running, saveStrategy, deleteStrategy, startEngine, stopEngine, runBacktest,
    autoPaused, pauseReason,
  } = useMindfulAlgo();
  const [view, setView] = useState('strategies'); // strategies | signals | backtest | log
  const [editingStrategy, setEditingStrategy] = useState(null);

  if (connectionStatus !== 'connected') {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 rounded-2xl flex items-center justify-center">
          <FaRobot className="text-2xl text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Algo Trading Engine</h3>
        <p className="text-sm text-gray-400">Connect to Angel One to build and run automated strategies</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {['strategies', 'signals', 'backtest', 'log'].map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-medium capitalize transition-all ${
                view === v ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {v}
              {v === 'signals' && signals.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-green-50 text-green-600 text-[9px]">
                  {signals.length}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {running ? (
            <button onClick={stopEngine} className="px-3 py-1.5 rounded-full bg-red-500 text-white text-[10px] font-medium flex items-center gap-1.5 hover:bg-red-600 transition-colors">
              <FaStop className="text-[8px]" /> Stop Engine
            </button>
          ) : (
            <button
              onClick={() => startEngine(strategies.filter(s => s.enabled))}
              disabled={strategies.filter(s => s.enabled).length === 0}
              className="px-3 py-1.5 rounded-full bg-blue-600 text-white text-[10px] font-medium flex items-center gap-1.5 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaPlay className="text-[8px]" /> Start Engine
            </button>
          )}
          <button
            onClick={() => setEditingStrategy({ name: '', conditions: [], action: 'BUY', enabled: false, autoExecute: false })}
            className="px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 text-[10px] font-medium flex items-center gap-1.5 hover:bg-gray-200 transition-colors"
          >
            <FaPlus className="text-[8px]" /> New Strategy
          </button>
        </div>
      </div>

      {/* Engine Status */}
      <div className={`px-3 py-2 rounded-lg text-[10px] font-medium flex items-center gap-2 ${
        running ? 'bg-green-50 text-green-600 border border-green-200' : 'bg-gray-50 text-gray-400 border border-gray-100'
      }`}>
        <div className={`w-1.5 h-1.5 rounded-full ${running ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
        {running ? `Engine running — ${strategies.filter(s => s.enabled).length} strategies active` : 'Engine stopped'}
      </div>

      {/* Mind Layer auto-pause banner */}
      {autoPaused && (
        <div className="px-3 py-2 rounded-lg text-[10px] font-medium flex items-center gap-2 bg-orange-50 text-orange-600 border border-orange-200">
          <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />
          Engine auto-paused by Mind Guard: {pauseReason}
        </div>
      )}

      {/* Content */}
      {view === 'strategies' && (
        <div className="space-y-3">
          {editingStrategy && (
            <StrategyBuilder
              strategy={editingStrategy}
              onSave={(s) => { saveStrategy(s); setEditingStrategy(null); }}
              onCancel={() => setEditingStrategy(null)}
            />
          )}
          {strategies.length === 0 && !editingStrategy ? (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 text-center">
              <p className="text-xs text-gray-400">No strategies yet. Create your first algo strategy.</p>
            </div>
          ) : (
            strategies.map(strategy => (
              <div key={strategy.id} className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${strategy.enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
                    <h4 className="text-sm font-semibold text-gray-900">{strategy.name}</h4>
                    {strategy.autoExecute && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600">AUTO</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setEditingStrategy(strategy)}
                      className="px-2 py-1 rounded text-[10px] text-gray-400 hover:text-blue-600 hover:bg-gray-50"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => saveStrategy({ ...strategy, enabled: !strategy.enabled })}
                      className={`px-2 py-1 rounded text-[10px] ${
                        strategy.enabled ? 'text-green-600 bg-green-50' : 'text-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      {strategy.enabled ? 'Enabled' : 'Disabled'}
                    </button>
                    <button
                      onClick={() => deleteStrategy(strategy.id)}
                      className="px-2 py-1 rounded text-[10px] text-gray-400 hover:text-red-500 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="text-[10px] text-gray-600">
                  {(strategy.conditions || []).length} conditions — Action: {strategy.action} —
                  {strategy.instrument ? ` ${strategy.instrument.symbol}` : ' No instrument'}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {view === 'signals' && <SignalMonitor signals={signals} />}
      {view === 'backtest' && <BacktestRunner strategies={strategies} onRun={runBacktest} results={backtestResults} />}
      {view === 'log' && <ExecutionLog entries={execLog} />}
    </div>
  );
}
